import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { UserPlus, CheckCircle, XCircle, Unlock } from "lucide-react";
import { Badge } from "../../../components/shared/Badge";
import { EnterprisePageShell } from "./EnterprisePageShell";
import {
  usePlatformEnterpriseUsers,
  useApproveUser, useDeclineUser, useUnlockPlatformUser,
} from "@platform/hooks";
import { useToast } from "@nthamizh/ui";

const TABS = ["All", "Active", "Pending", "Inactive", "Locked"];

interface EntUser {
  id: string; name: string; email: string; jobTitle: string | null;
  isLocked: boolean; failedLoginAttempts: number;
  status: "active" | "pending" | "inactive";
  memberSince: string;
  roles: { roleId: string; roleName: string; roleKey: string; moduleKey: string | null }[];
}

export function PlatformEnterpriseUsersPage() {
  const { id: enterpriseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");

  const { data: users = [], isLoading } = usePlatformEnterpriseUsers(enterpriseId ?? null);

  const approve = useApproveUser();
  const decline = useDeclineUser();
  const unlock = useUnlockPlatformUser();

  const typedUsers = users as EntUser[];

  const searched = typedUsers.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  const tabCounts = [
    searched.length,
    searched.filter((u) => u.status === "active" && !u.isLocked).length,
    searched.filter((u) => u.status === "pending").length,
    searched.filter((u) => u.status === "inactive").length,
    searched.filter((u) => u.isLocked).length,
  ];

  const filtered = searched.filter((u) => {
    if (activeTab === 1) return u.status === "active" && !u.isLocked;
    if (activeTab === 2) return u.status === "pending";
    if (activeTab === 3) return u.status === "inactive";
    if (activeTab === 4) return u.isLocked;
    return true;
  });

  const statusBadge = (status: string, locked: boolean) => {
    if (locked) return <Badge variant="red">Locked</Badge>;
    if (status === "active") return <Badge variant="green">Active</Badge>;
    if (status === "pending") return <Badge variant="amber">Pending</Badge>;
    return <Badge variant="gray">Inactive</Badge>;
  };

  const handleApprove = async (u: EntUser) => {
    try { await approve.mutateAsync(u.id); toast({ title: `${u.name} approved`, variant: "success" }); }
    catch { toast({ title: "Failed to approve", variant: "error" }); }
  };

  const handleDecline = async (u: EntUser) => {
    try { await decline.mutateAsync(u.id); toast({ title: `${u.name} declined`, variant: "success" }); }
    catch { toast({ title: "Failed to decline", variant: "error" }); }
  };

  const handleUnlock = async (u: EntUser) => {
    try { await unlock.mutateAsync(u.id); toast({ title: `${u.name} unlocked`, variant: "success" }); }
    catch { toast({ title: "Failed to unlock", variant: "error" }); }
  };

  return (
    <EnterprisePageShell
      enterpriseId={enterpriseId ?? ""}
      pageName="Users"
      action={
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
          <UserPlus className="w-3.5 h-3.5" /> Add user
        </button>
      }
    >
      {/* Search + tabs */}
      <div className="flex items-center gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users…"
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 w-60"
        />
      </div>

      <div className="flex border-b border-ds-border">
        {TABS.map((tab, i) => (
          <button key={tab} onClick={() => setActiveTab(i)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === i ? "border-indigo-600 text-indigo-700" : "border-transparent text-ds-text-secondary hover:text-ds-text"}`}>
            {tab} ({tabCounts[i]})
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">No users in this category.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ds-border bg-ds-bg">
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">User</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Roles</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Status</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Member since</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-border">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-ds-bg/50">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold flex-shrink-0">
                        {u.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-ds-text">{u.name}</p>
                        <p className="text-xs text-ds-text-muted">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length > 0
                        ? u.roles.map((r) => <Badge key={r.roleId} variant="blue">{r.roleName}</Badge>)
                        : <span className="text-xs text-ds-text-muted">No roles</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">{statusBadge(u.status, u.isLocked)}</td>
                  <td className="px-5 py-3.5 text-xs text-ds-text-muted">
                    {new Date(u.memberSince).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      {/* Status-specific quick actions */}
                      {u.status === "pending" && (
                        <>
                          <button onClick={() => handleApprove(u)} disabled={approve.isPending}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-medium disabled:opacity-50">
                            <CheckCircle className="w-3 h-3" /> Approve
                          </button>
                          <button onClick={() => handleDecline(u)} disabled={decline.isPending}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-medium disabled:opacity-50">
                            <XCircle className="w-3 h-3" /> Decline
                          </button>
                        </>
                      )}
                      {u.isLocked && (
                        <button onClick={() => handleUnlock(u)} disabled={unlock.isPending}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-medium disabled:opacity-50">
                          <Unlock className="w-3 h-3" /> Unlock
                        </button>
                      )}
                      {/* Manage — stays within enterprise context */}
                      <button
                        onClick={() => navigate(`/platform/enterprises/${enterpriseId}/users/${u.id}`)}
                        className="text-xs text-indigo-600 font-medium hover:underline whitespace-nowrap"
                      >
                        Manage →
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </EnterprisePageShell>
  );
}
